import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { createPortal } from 'react-dom';

const ToastContext = createContext(null);

/**
 * Toast types: 'success' | 'error' | 'info' | 'warning'
 */
const toastStyles = {
    success: {
        bg: 'linear-gradient(135deg, rgba(16, 185, 129, 0.95) 0%, rgba(5, 150, 105, 0.95) 100%)',
        border: 'rgba(52, 211, 153, 0.3)',
        icon: '✅',
        shadow: '0 8px 32px rgba(16, 185, 129, 0.3)',
    },
    error: {
        bg: 'linear-gradient(135deg, rgba(239, 68, 68, 0.95) 0%, rgba(220, 38, 38, 0.95) 100%)',
        border: 'rgba(248, 113, 113, 0.3)',
        icon: '❌',
        shadow: '0 8px 32px rgba(239, 68, 68, 0.3)',
    },
    info: {
        bg: 'linear-gradient(135deg, rgba(59, 130, 246, 0.95) 0%, rgba(37, 99, 235, 0.95) 100%)',
        border: 'rgba(96, 165, 250, 0.3)',
        icon: 'ℹ️',
        shadow: '0 8px 32px rgba(59, 130, 246, 0.3)',
    },
    warning: {
        bg: 'linear-gradient(135deg, rgba(245, 158, 11, 0.95) 0%, rgba(217, 119, 6, 0.95) 100%)',
        border: 'rgba(251, 191, 36, 0.3)',
        icon: '⚠️',
        shadow: '0 8px 32px rgba(245, 158, 11, 0.3)',
    },
};

let toastId = 0;

function ToastItem({ toast, onRemove }) {
    const [isExiting, setIsExiting] = useState(false);
    const style = toastStyles[toast.type] || toastStyles.info;

    useEffect(() => {
        const timer = setTimeout(() => {
            setIsExiting(true);
            setTimeout(() => onRemove(toast.id), 300);
        }, toast.duration || 3000);
        return () => clearTimeout(timer);
    }, [toast.id, toast.duration, onRemove]);

    const handleClose = () => {
        setIsExiting(true);
        setTimeout(() => onRemove(toast.id), 300);
    };

    return (
        <div
            className={`flex items-center gap-3 px-5 py-3.5 rounded-2xl text-white max-w-sm w-full cursor-pointer select-none`}
            style={{
                background: style.bg,
                border: `1px solid ${style.border}`,
                boxShadow: style.shadow,
                backdropFilter: 'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
                animation: isExiting
                    ? 'toastSlideOut 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards'
                    : 'toastSlideIn 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
            }}
            onClick={handleClose}
        >
            <span className="text-lg flex-shrink-0">{style.icon}</span>
            <p className="text-sm font-medium leading-snug flex-1">{toast.message}</p>
            <button
                onClick={(e) => { e.stopPropagation(); handleClose(); }}
                className="text-white/60 hover:text-white transition-colors flex-shrink-0"
            >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
            </button>
        </div>
    );
}

function ToastContainer({ toasts, removeToast }) {
    if (toasts.length === 0) return null;

    return createPortal(
        <div
            className="fixed top-4 right-4 z-[99999] flex flex-col gap-2.5 pointer-events-none"
            style={{ maxWidth: '400px', width: 'calc(100vw - 32px)' }}
        >
            {toasts.map((toast) => (
                <div key={toast.id} className="pointer-events-auto">
                    <ToastItem toast={toast} onRemove={removeToast} />
                </div>
            ))}

            <style>{`
                @keyframes toastSlideIn {
                    from {
                        opacity: 0;
                        transform: translateX(100px) scale(0.95);
                    }
                    to {
                        opacity: 1;
                        transform: translateX(0) scale(1);
                    }
                }
                @keyframes toastSlideOut {
                    from {
                        opacity: 1;
                        transform: translateX(0) scale(1);
                    }
                    to {
                        opacity: 0;
                        transform: translateX(100px) scale(0.95);
                    }
                }
            `}</style>
        </div>,
        document.body
    );
}

export function ToastProvider({ children }) {
    const [toasts, setToasts] = useState([]);

    const addToast = useCallback((message, type = 'info', duration = 3000) => {
        const id = ++toastId;
        setToasts((prev) => [...prev, { id, message, type, duration }]);
    }, []);

    const removeToast = useCallback((id) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
    }, []);

    const toastMethods = React.useMemo(() => ({
        success: (msg, dur) => addToast(msg, 'success', dur),
        error: (msg, dur) => addToast(msg, 'error', dur),
        info: (msg, dur) => addToast(msg, 'info', dur),
        warning: (msg, dur) => addToast(msg, 'warning', dur),
    }), [addToast]);

    return (
        <ToastContext.Provider value={toastMethods}>
            {children}
            <ToastContainer toasts={toasts} removeToast={removeToast} />
        </ToastContext.Provider>
    );
}

export function useToast() {
    const context = useContext(ToastContext);
    if (!context) {
        // Fallback if outside provider — use alert
        return {
            success: (msg) => window.alert(msg),
            error: (msg) => window.alert(msg),
            info: (msg) => window.alert(msg),
            warning: (msg) => window.alert(msg),
        };
    }
    return context;
}

export default ToastProvider;
